import { useEffect } from 'react'
import { format } from 'date-fns'
import { useTravelSearchContext } from '../../context/TravelSearchProvider'
import { useTravelDates } from './useTravelDates'

export function useSyncDatesFallback() {
  const { state, dispatch } = useTravelSearchContext()
  const { outboundTravelDates, homeboundTravelDates } = useTravelDates()

  const { outboundDate, homeboundDate } = state.selectedDates ?? {}

  useEffect(() => {
    if (outboundDate && !outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd'))) {
      return
    }

    if (
      outboundDate &&
      outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd')) &&
      outboundTravelDates.defaultDate &&
      !outboundTravelDates?.disabledDates.includes(outboundTravelDates.defaultDate)
    ) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)
      dispatch({
        type: 'SET_DATES',
        dates: {
          outboundDate: fallbackDate,
          homeboundDate,
        },
      })
      return
    }

    //prevent customer for selecting disabled date in outbound datepicker
    if (outboundDate && outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd'))) {
      const fallbackOutbound = getNextAvailableDate(
        format(outboundDate, 'yyyy-MM-dd'),
        outboundTravelDates.disabledDates,
        true,
        outboundTravelDates?.dates?.toDate
      )
      dispatch({
        type: 'SET_DATES',
        dates: {
          outboundDate: fallbackOutbound,
          homeboundDate,
        },
      })
      return
    }

    if (!outboundDate && outboundTravelDates?.defaultDate) {
      const fallbackDate = new Date(outboundTravelDates.defaultDate)

      dispatch({
        type: 'SET_DATES',
        dates: {
          outboundDate: fallbackDate,
          homeboundDate,
        },
      })
    }
  }, [outboundTravelDates, outboundDate, dispatch])

  useEffect(() => {
    if (state.isOneWay) return

    if (outboundDate && outboundTravelDates?.disabledDates.includes(format(outboundDate, 'yyyy-MM-dd'))) {
      return
    }

    if (
      homeboundDate &&
      !homeboundTravelDates?.disabledDates.includes(format(homeboundDate, 'yyyy-MM-dd')) &&
      outboundDate &&
      homeboundDate >= outboundDate
    ) {
      return
    }

    //prevent customer for selecting disabled date in homebound datepicker
    if (homeboundDate && homeboundTravelDates?.disabledDates.includes(format(homeboundDate, 'yyyy-MM-dd'))) {
      const fallbackHomebound = getNextAvailableDate(
        homeboundTravelDates.defaultDate,
        homeboundTravelDates.disabledDates,
        false,
        homeboundTravelDates?.dates?.toDate
      )
      dispatch({
        type: 'SET_DATES',
        dates: {
          outboundDate,
          homeboundDate: fallbackHomebound,
        },
      })
      return
    }
    // Otherwise fallback to next available date if present
    else if (homeboundTravelDates && outboundDate) {
      const fallbackHomebound = getNextAvailableDate(
        format(outboundDate, 'yyyy-MM-dd'),
        homeboundTravelDates.disabledDates,
        false,
        homeboundTravelDates.dates.toDate,
        false
      )
      dispatch({
        type: 'SET_DATES',
        dates: {
          outboundDate,
          homeboundDate: fallbackHomebound,
        },
      })
    }
  }, [homeboundTravelDates, homeboundDate, outboundDate, state.isOneWay, dispatch])
}

function getNextAvailableDate(
  startDate: string,
  unavailableDates: string[],
  invers: boolean,
  endDate?: string,
  allowSameDate = true
): Date {
  const originalStart = new Date(startDate)

  const current = new Date(startDate)
  if (!invers && !allowSameDate) {
    current.setDate(current.getDate() + 1)
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    const currentStr = format(current, 'yyyy-MM-dd')
    if (!unavailableDates.includes(currentStr)) {
      return current
    }
    if (endDate && currentStr === endDate) {
      return originalStart
    }
    current.setDate(current.getDate() + (invers ? -1 : 1))
  }
}
